import React, { useEffect, useState, useMemo } from 'react';
import { Typography, Card, Button, Select, DatePicker, Space, List, Tag, Divider, Steps, InputNumber, Modal, Form, Input, Switch, Table, Popconfirm } from 'antd';
import L from 'leaflet';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { showMessage } from '../../../App';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

const TRIP_COLOR_PALETTE = ['#2563eb', '#16a34a', '#a855f7', '#f97316', '#8b5cf6', '#0ea5e9', '#f59e0b', '#14b8a6'];
const GOLDEN_RATIO_CONJUGATE = 0.618033988749895;

function hslToHex(h, s, l) {
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const lig = Math.max(0, Math.min(100, l)) / 100;
  const a = sat * Math.min(lig, 1 - lig);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = lig - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function getTripColorByIndex(index) {
  if (index < TRIP_COLOR_PALETTE.length) {
    return TRIP_COLOR_PALETTE[index];
  }
  const dynamicIndex = index - TRIP_COLOR_PALETTE.length + 1;
  const hue = (dynamicIndex * GOLDEN_RATIO_CONJUGATE % 1) * 360;
  return hslToHex(hue, 65, 55);
}

const VehicleHistory = () => {
  const { token } = useAuth();
  const [dateRange, setDateRange] = useState([dayjs().subtract(1, 'day'), dayjs()]);
  const [geotabDbOptions, setGeotabDbOptions] = useState([]);
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [trips, setTrips] = useState([]);
  const [selectedTripIds, setSelectedTripIds] = useState([]);
  const [minStopDuration, setMinStopDuration] = useState(null);
  const [maxStopDuration, setMaxStopDuration] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [selectedBasemap, setSelectedBasemap] = useState('osm');
  
  // Campaign management state
  const [adsPerMile, setAdsPerMile] = useState(1);
  const [savedCampaigns, setSavedCampaigns] = useState([]);
  const [loadingSavedCampaigns, setLoadingSavedCampaigns] = useState(false);
  const [saveCampaignModalOpen, setSaveCampaignModalOpen] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [campaignForm] = Form.useForm();
  
  // Auto-pull settings
  const [autoPullEnabled, setAutoPullEnabled] = useState(false);
  const [autoPullDays, setAutoPullDays] = useState(7);
  const [autoPullStartDate, setAutoPullStartDate] = useState(null);

  const CENTER = [35.5175, -86.5804];
  
  const providers = useMemo(() => ({
    osm: {
      id: 'osm',
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '© OpenStreetMap contributors'
    },
    esri: {
      id: 'esri',
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Tiles © Esri'
    }
  }), []);

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  const fetchGeotabAccounts = async () => {
    try {
      const { data } = await axios.get('/api/integrations/geotab/accounts', { headers: authHeader() });
      const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      let opts = items
        .map(a => a?.database)
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .map(db => ({ value: db, label: db }));
      
      setGeotabDbOptions(opts);
      setSelectedDatabases(opts.map(o => o.value));
    } catch (e) {
      setGeotabDbOptions([]);
      setSelectedDatabases([]);
    }
  };

  const fetchDevices = async () => {
    if (!selectedDatabases.length) {
      showMessage('warning', 'Select at least one database');
      return;
    }
    try {
      setLoadingDevices(true);
      const query = new URLSearchParams();
      query.set('databases', selectedDatabases.join(','));
      const { data } = await axios.get(`/api/integrations/geotab/devices?${query.toString()}`, { headers: authHeader() });
      const items = Array.isArray(data?.devices) ? data.devices : [];
      setDevices(items);
      if (items.length > 0) {
        showMessage('success', `Loaded ${items.length} vehicle(s)`);
      } else {
        showMessage('info', 'No vehicles found');
      }
    } catch (e) {
      showMessage('error', e?.response?.data?.error || 'Failed to load vehicles');
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchTrips = async () => {
    if (!selectedDeviceIds || selectedDeviceIds.length === 0) {
      showMessage('warning', 'Select at least one vehicle');
      return;
    }
    if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
      showMessage('warning', 'Select a date range');
      return;
    }
    try {
      setLoadingTrips(true);
      const query = new URLSearchParams();
      query.set('deviceIds', selectedDeviceIds.join(','));
      query.set('databases', selectedDatabases.join(','));
      query.set('startDate', dateRange[0].startOf('day').toISOString());
      query.set('endDate', dateRange[1].endOf('day').toISOString());
      
      const { data } = await axios.get(`/api/integrations/geotab/trips?${query.toString()}`, { headers: authHeader() });
      const items = Array.isArray(data?.trips) ? data.trips : [];
      const filtered = items.filter((trip) => {
        const duration = Number(trip?.durationMinutes ?? 0);
        if (minStopDuration != null && duration < minStopDuration) return false;
        if (maxStopDuration != null && duration > maxStopDuration) return false;
        return true;
      });

      setTrips(filtered);

      if (items.length > 0) {
        const filteredMsg = filtered.length === items.length
          ? `${filtered.length}`
          : `${filtered.length} of ${items.length}`;
        showMessage('success', `Found ${filteredMsg} trip(s)`);

        if (filtered.length > 0) {
          setSelectedTripIds([filtered[0].id]);
        } else {
          showMessage('info', 'No trips matched the stop duration filters');
          setSelectedTripIds([]);
        }
      } else {
        showMessage('info', 'No trips found for the selected vehicles and date range');
        setSelectedTripIds([]);
      }
    } catch (e) {
      showMessage('error', e?.response?.data?.error || 'Failed to load trips');
      setTrips([]);
      setSelectedTripIds([]);
    } finally {
      setLoadingTrips(false);
    }
  };

  useEffect(() => {
    fetchGeotabAccounts();
    fetchSavedCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure selected trip ids stay in sync with available trips
  useEffect(() => {
    setSelectedTripIds(prev => prev.filter(id => trips.some(t => t.id === id)));
  }, [trips]);

  // Auto-fit bounds when selected trips change
  useEffect(() => {
    if (!mapRef || selectedTripIds.length === 0) return;
    const selectedTrips = trips.filter(t => selectedTripIds.includes(t.id));
    const points = selectedTrips
      .flatMap(trip => (Array.isArray(trip.route) ? trip.route : []))
      .filter(p => typeof p.latitude === 'number' && typeof p.longitude === 'number')
      .map(p => [p.latitude, p.longitude]);
    if (points.length === 0) return;
    try {
      if (points.length === 1) {
        mapRef.setView(points[0], 13);
      } else {
        const bounds = L.latLngBounds(points);
        mapRef.fitBounds(bounds, { padding: [40, 40] });
      }
    } catch {}
  }, [selectedTripIds, trips, mapRef]);

  const tripsByDevice = useMemo(() => {
    const grouped = new Map();
    trips.forEach(trip => {
      const key = trip.deviceId || trip.device;
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(trip);
    });
    return grouped;
  }, [trips]);

  const tripMetaMap = useMemo(() => {
    const map = new Map();
    trips.forEach((trip, idx) => {
      map.set(trip.id, {
        index: idx,
        label: `Trip ${idx + 1}`,
        color: getTripColorByIndex(idx),
      });
    });
    return map;
  }, [trips]);

  const getTripLabel = (tripId) => tripMetaMap.get(tripId)?.label || 'Trip';
  const getTripColor = (tripId) => tripMetaMap.get(tripId)?.color || '#2563eb';

  const formatAddress = (addressObj) => {
    if (!addressObj) return 'Unknown location';
    if (typeof addressObj === 'string') return addressObj;
    const { address, city, state, postcode, country } = addressObj;
    if (address) return address;
    const parts = [city, state, postcode, country].filter(Boolean);
    return parts.length ? parts.join(', ') : 'Unknown location';
  };

  const toggleTripSelection = (tripId) => {
    setSelectedTripIds(prev => {
      if (prev.includes(tripId)) {
        return prev.filter(id => id !== tripId);
      }
      return [...prev, tripId];
    });
  };

  const clearTripsSelection = () => {
    setSelectedTripIds([]);
  };

  const selectedTrips = useMemo(
    () => trips.filter(trip => selectedTripIds.includes(trip.id)),
    [trips, selectedTripIds]
  );

  const selectAllVehicles = () => {
    if (!Array.isArray(devices) || devices.length === 0) return;
    const allIds = devices.map(device => device.id).filter(Boolean);
    setSelectedDeviceIds(allIds);
  };

  const clearAllVehicles = () => {
    setSelectedDeviceIds([]);
    setTrips([]);
    clearTripsSelection();
  };

  const selectAllTrips = () => {
    if (!Array.isArray(trips) || trips.length === 0) return;
    const allIds = trips.map(trip => trip.id).filter(Boolean);
    setSelectedTripIds(allIds);
  };

  const buildCampaignPayload = () => {
    if (selectedTrips.length === 0) {
      showMessage('warning', 'Select at least one trip to export');
      return null;
    }

    const MAX_POINTS_PER_TRIP = 50;
    const waypoints = [];
    const tripSummaries = [];

    selectedTrips.forEach((trip) => {
      const tripLabel = getTripLabel(trip.id);
      const color = getTripColor(trip.id);
      const route = Array.isArray(trip.route) ? trip.route : [];
      if (route.length === 0) {
        return;
      }

      const samplingStep = Math.max(1, Math.round(route.length / MAX_POINTS_PER_TRIP));
      route.forEach((point, idx) => {
        if (idx !== 0 && idx !== route.length - 1 && idx % samplingStep !== 0) {
          return;
        }
        const lat = Number(point.latitude);
        const lng = Number(point.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
          return;
        }
        waypoints.push({
          id: `${trip.id}-${idx}`,
          name: `${tripLabel} · Point ${idx + 1}`,
          tripId: trip.id,
          tripLabel,
          lat,
          lng,
          sequence: idx,
          device: trip.device || null,
          timestamp: point.dateTime || null,
        });
      });

      const startPoint = route[0];
      const endPoint = route[route.length - 1];
      tripSummaries.push({
        id: trip.id,
        label: tripLabel,
        color,
        device: trip.device || null,
        database: trip.database || null,
        start: trip.start || null,
        stop: trip.stop || null,
        startPoint: startPoint ? { lat: startPoint.latitude, lng: startPoint.longitude } : null,
        endPoint: endPoint ? { lat: endPoint.latitude, lng: endPoint.longitude } : null,
        totalPoints: route.length,
      });
    });

    if (waypoints.length === 0) {
      showMessage('warning', 'Selected trips do not contain GPS points to export');
      return null;
    }

    return {
      source: 'VehicleHistory',
      generatedAt: new Date().toISOString(),
      tripCount: selectedTrips.length,
      waypoints,
      trips: tripSummaries,
    };
  };

  const fetchSavedCampaigns = async () => {
    try {
      setLoadingSavedCampaigns(true);
      const { data } = await axios.get('/api/vehicle-campaigns', { headers: authHeader() });
      setSavedCampaigns(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load saved campaigns:', e);
      setSavedCampaigns([]);
    } finally {
      setLoadingSavedCampaigns(false);
    }
  };

  const openSaveCampaignModal = () => {
    setEditingCampaignId(null);
    campaignForm.resetFields();
    campaignForm.setFieldsValue({
      ads_per_mile: adsPerMile,
      auto_pull_enabled: autoPullEnabled,
      auto_pull_days: autoPullDays,
      start_date: autoPullStartDate,
    });
    setSaveCampaignModalOpen(true);
  };

  const openEditCampaignModal = (campaign) => {
    setEditingCampaignId(campaign.id);
    campaignForm.setFieldsValue({
      name: campaign.name,
      description: campaign.description,
      ads_per_mile: campaign.ads_per_mile,
      auto_pull_enabled: campaign.auto_pull_enabled,
      auto_pull_days: campaign.auto_pull_days,
      start_date: campaign.start_date ? dayjs(campaign.start_date) : null,
      end_date: campaign.end_date ? dayjs(campaign.end_date) : null,
    });
    setSaveCampaignModalOpen(true);
  };

  const handleSaveCampaign = async () => {
    try {
      const values = await campaignForm.validateFields();
      setSavingCampaign(true);
      
      const payload = buildCampaignPayload();
      if (!payload) {
        setSavingCampaign(false);
        return;
      }

      const campaignData = {
        name: values.name,
        description: values.description || null,
        ads_per_mile: values.ads_per_mile || 1,
        route_data: payload,
        status: 'draft',
        auto_pull_enabled: values.auto_pull_enabled || false,
        auto_pull_days: values.auto_pull_days || null,
        start_date: values.start_date ? dayjs(values.start_date).toISOString() : null,
        end_date: values.end_date ? dayjs(values.end_date).toISOString() : null,
      };

      if (editingCampaignId) {
        await axios.patch(`/api/vehicle-campaigns/${editingCampaignId}`, campaignData, { headers: authHeader() });
        showMessage('success', 'Campaign updated successfully');
      } else {
        await axios.post('/api/vehicle-campaigns', campaignData, { headers: authHeader() });
        showMessage('success', 'Campaign saved successfully');
      }
      setSaveCampaignModalOpen(false);
      campaignForm.resetFields();
      fetchSavedCampaigns();
    } catch (e) {
      if (e?.errorFields) return;
      console.error('Save campaign error:', e?.response?.data || e?.message);
      showMessage('error', e?.response?.data?.error || 'Failed to save campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  const handleUpdateCampaignStatus = async (campaignId, newStatus) => {
    try {
      await axios.patch(`/api/vehicle-campaigns/${campaignId}`, 
        { status: newStatus }, 
        { headers: authHeader() }
      );
      showMessage('success', `Campaign ${newStatus === 'active' ? 'started' : 'stopped'}`);
      fetchSavedCampaigns();
    } catch (e) {
      showMessage('error', e?.response?.data?.error || 'Failed to update campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await axios.delete(`/api/vehicle-campaigns/${campaignId}`, { headers: authHeader() });
      showMessage('success', 'Campaign deleted');
      fetchSavedCampaigns();
    } catch (e) {
      showMessage('error', e?.response?.data?.error || 'Failed to delete campaign');
    }
  };

  const handleExportToCampaigns = (platform) => {
    const payload = buildCampaignPayload();
    if (!payload) return;

    const exportPayload = { ...payload, platform, adsPerMile };
    try {
      sessionStorage.setItem('pm_campaign_import_route', JSON.stringify(exportPayload));
    } catch {}

    let destination;
    if (platform === 'facebook') {
      destination = '/client/campaigns?launch=facebook';
    } else if (platform === 'google') {
      destination = '/client/campaigns?launch=google';
    } else {
      destination = '/client/campaigns/new-camp-demo';
    }
    window.location.href = destination;
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>Vehicle History</Title>
      <Paragraph type="secondary">View vehicle routes and trips from Geotab GPS data</Paragraph>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title="Filters">
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* Filter Cards Row */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {/* Date Range Card */}
              <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, minWidth: 250, flexShrink: 0 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Date Range</Text>
                <DatePicker.RangePicker
                  size="small"
                  value={dateRange}
                  onChange={(values) => {
                    if (!values || values.length !== 2 || !values[0] || !values[1]) {
                      setDateRange([dayjs().subtract(1, 'day'), dayjs()]);
                    } else {
                      setDateRange(values);
                    }
                  }}
                  allowClear={false}
                  style={{ width: '100%' }}
                />
              </div>

              {/* Geotab Databases Card */}
              {geotabDbOptions.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, minWidth: 200, flexShrink: 0 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Databases</Text>
                  <Select
                    mode="multiple"
                    allowClear
                    size="small"
                    style={{ width: '100%' }}
                    value={selectedDatabases}
                    onChange={setSelectedDatabases}
                    options={geotabDbOptions}
                    placeholder="Select databases"
                  />
                </div>
              )}

              {/* Min Stop Duration Card */}
              <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, minWidth: 160, flexShrink: 0 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Min Stop (min)</Text>
                <InputNumber
                  size="small"
                  min={0}
                  style={{ width: '100%' }}
                  value={minStopDuration}
                  placeholder="No minimum"
                  onChange={(value) => setMinStopDuration(value ?? null)}
                />
              </div>

              {/* Max Stop Duration Card */}
              <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, minWidth: 160, flexShrink: 0 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Max Stop (min)</Text>
                <InputNumber
                  size="small"
                  min={0}
                  style={{ width: '100%' }}
                  value={maxStopDuration}
                  placeholder="No maximum"
                  onChange={(value) => setMaxStopDuration(value ?? null)}
                />
              </div>
            </div>

            {/* Load Vehicles Button */}
            <Button
              type="primary"
              size="large"
              onClick={fetchDevices}
              loading={loadingDevices}
              disabled={geotabDbOptions.length > 0 && selectedDatabases.length === 0}
            >
              Load Vehicles
            </Button>

            {/* Vehicle Selection Card */}
            {devices.length > 0 && (
              <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, padding: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>Select Vehicles</Text>
                <Select
                  mode="multiple"
                  allowClear
                  size="large"
                  style={{ width: '100%', marginBottom: 12 }}
                  value={selectedDeviceIds}
                  onChange={(values) => {
                    setSelectedDeviceIds(values);
                    if (values.length === 0) {
                      setTrips([]);
                      clearTripsSelection();
                    }
                  }}
                  placeholder="Choose one or more vehicles"
                  maxTagCount="responsive"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={devices.map(d => ({
                    value: d.id,
                    label: d.name || d.serialNumber || d.id,
                    title: d.database ? `${d.name || d.serialNumber || d.id} (${d.database})` : undefined,
                  }))}
                />
                <Space size={8} wrap>
                  <Button size="small" onClick={selectAllVehicles} disabled={devices.length === 0}>
                    Select All Vehicles
                  </Button>
                  <Button size="small" onClick={clearAllVehicles} disabled={selectedDeviceIds.length === 0}>
                    Deselect All
                  </Button>
                </Space>
              </div>
            )}

            {/* Load Trips Button */}
            {devices.length > 0 && selectedDeviceIds.length > 0 && (
              <Button
                type="primary"
                size="large"
                onClick={fetchTrips}
                loading={loadingTrips}
                disabled={selectedDeviceIds.length === 0}
              >
                Load Trips
              </Button>
            )}
          </Space>
        </Card>

        {trips.length > 0 && (
          <Card title="Trip History">
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Space size={8} wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button size="small" onClick={selectAllTrips} disabled={trips.length === 0 || selectedTripIds.length === trips.length}>
                  Select All Trips
                </Button>
                <Button size="small" onClick={clearTripsSelection} disabled={selectedTripIds.length === 0}>
                  Deselect All
                </Button>
              </Space>
              {[...tripsByDevice.entries()].map(([deviceId, deviceTrips], idx) => (
                <div key={deviceId}>
                  {idx > 0 && <Divider style={{ margin: '12px 0' }} />}
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                      <Text strong>{deviceTrips[0]?.device || devices.find(d => d.id === deviceId)?.name || deviceId}</Text>
                      <Tag color={deviceTrips.some(trip => selectedTripIds.includes(trip.id)) ? 'blue' : undefined}>
                        {deviceTrips.length} trip(s)
                      </Tag>
                    </Space>
                    <List
                      size="small"
                      bordered
                      dataSource={deviceTrips}
                      renderItem={(trip) => {
                        const isSelected = selectedTripIds.includes(trip.id);
                        const tripLabel = getTripLabel(trip.id);
                        const color = getTripColor(trip.id);
                        return (
                          <List.Item
                            onClick={() => toggleTripSelection(trip.id)}
                            style={{
                              cursor: 'pointer',
                              background: isSelected ? '#e6f7ff' : 'transparent',
                            }}
                          >
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                              <Space size={6} wrap align="center">
                                <Tag color={color} style={{ opacity: isSelected ? 1 : 0.7 }}>{tripLabel}</Tag>
                                {isSelected && <Tag color={color}>Selected</Tag>}
                                {trip.database && <Tag>{trip.database}</Tag>}
                              </Space>
                              <Text strong>{dayjs(trip.start).format('M/D/YYYY')}</Text>
                              <Text type="secondary">
                                Start: {trip.start ? dayjs(trip.start).format('M/D/YYYY h:mm A') : '—'}
                              </Text>
                              <Text type="secondary">
                                End: {trip.stop ? dayjs(trip.stop).format('M/D/YYYY h:mm A') : '—'}
                              </Text>
                              <Text type="secondary">
                                Start Address: {formatAddress(trip.startAddress)}
                              </Text>
                              <Text type="secondary">
                                End Address: {formatAddress(trip.endAddress)}
                              </Text>
                              <Text type="secondary">
                                Distance: {trip.distance ? `${trip.distance.toFixed(1)} km` : '—'} | 
                                Duration: {trip.durationMinutes ? `${Math.floor(trip.durationMinutes)} min` : '—'}
                              </Text>
                            </Space>
                          </List.Item>
                        );
                      }}
                    />
                  </Space>
                </div>
              ))}
            </Space>
          </Card>
        )}

        <Card title="Map View">
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Space>
              <Text type="secondary">Basemap:</Text>
              <Select
                size="small"
                style={{ width: 160 }}
                value={selectedBasemap}
                onChange={setSelectedBasemap}
                options={[
                  { value: 'osm', label: 'OpenStreetMap' },
                  { value: 'esri', label: 'Esri Streets' }
                ]}
              />
            </Space>
            <div style={{ height: '70vh', width: '100%' }}>
              <MapContainer center={CENTER} zoom={7} whenCreated={setMapRef} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  key={selectedBasemap}
                  url={providers[selectedBasemap].url}
                  attribution={providers[selectedBasemap].attribution}
                />
                {selectedTrips.map(trip => {
                  const route = Array.isArray(trip.route) ? trip.route : [];
                  if (route.length === 0) return null;
                  const color = getTripColor(trip.id);
                  const tripLabel = getTripLabel(trip.id);
                  const positions = route.map(p => [p.latitude, p.longitude]);
                  const startPoint = route[0];
                  const endPoint = route[route.length - 1];
                  return (
                    <React.Fragment key={trip.id}>
                      {positions.length > 1 && (
                        <Polyline positions={positions} pathOptions={{ color, weight: 4, opacity: 0.9 }} />
                      )}
                      {startPoint && (
                        <CircleMarker
                          center={[startPoint.latitude, startPoint.longitude]}
                          radius={7}
                          pathOptions={{ color, fillColor: color, fillOpacity: 0.9 }}
                        >
                          <Tooltip>
                            {`${tripLabel} · Start\nDevice: ${trip.device || 'Unknown'}\n${formatAddress(trip.startAddress)}`}
                          </Tooltip>
                        </CircleMarker>
                      )}
                      {endPoint && (
                        <CircleMarker
                          center={[endPoint.latitude, endPoint.longitude]}
                          radius={7}
                          pathOptions={{ color, fillColor: '#ffffff', fillOpacity: 1, weight: 2 }}
                        >
                          <Tooltip>
                            {`${tripLabel} · End\nDevice: ${trip.device || 'Unknown'}\n${formatAddress(trip.endAddress)}`}
                          </Tooltip>
                        </CircleMarker>
                      )}
                      {route.slice(1, -1).map((p, idx) => (
                        <CircleMarker
                          key={`${trip.id}-wp-${idx}`}
                          center={[p.latitude, p.longitude]}
                          radius={3}
                          pathOptions={{ color, fillColor: color, fillOpacity: 0.6 }}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </MapContainer>
            </div>
          </Space>
        </Card>

        <Card title="Campaign Configuration">
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div>
                  <Text strong>Ads Per Mile</Text>
                  <Paragraph type="secondary" style={{ margin: '4px 0 8px 0', fontSize: 13 }}>
                    Configure how many ad drops to place per mile of route
                  </Paragraph>
                  <InputNumber
                    min={0.1}
                    max={10}
                    step={0.1}
                    value={adsPerMile}
                    onChange={setAdsPerMile}
                    style={{ width: 200 }}
                    addonAfter="per mile"
                  />
                </div>
                
                <Divider style={{ margin: '8px 0' }} />
                
                <div>
                  <Text strong>Auto-Pull Routes</Text>
                  <Paragraph type="secondary" style={{ margin: '4px 0 8px 0', fontSize: 13 }}>
                    Automatically pull routes from the past N days for this campaign
                  </Paragraph>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Space>
                      <Switch 
                        checked={autoPullEnabled} 
                        onChange={setAutoPullEnabled}
                      />
                      <Text>Enable automatic route pulling</Text>
                    </Space>
                    {autoPullEnabled && (
                      <>
                        <Space>
                          <Text>Pull routes from past</Text>
                          <InputNumber
                            min={1}
                            max={90}
                            value={autoPullDays}
                            onChange={setAutoPullDays}
                            style={{ width: 80 }}
                          />
                          <Text>days</Text>
                        </Space>
                        <Space>
                          <Text>Start date:</Text>
                          <DatePicker
                            value={autoPullStartDate}
                            onChange={setAutoPullStartDate}
                            style={{ width: 200 }}
                          />
                        </Space>
                      </>
                    )}
                  </Space>
                </div>
              </Space>
            </div>

            <Steps
              responsive
              items={[
                {
                  title: 'Select Trips',
                  description: selectedTripIds.length > 0
                    ? `${selectedTripIds.length} trip${selectedTripIds.length === 1 ? '' : 's'} selected`
                    : 'Pick trip routes from the list above',
                  status: selectedTripIds.length > 0 ? 'finish' : 'process',
                },
                {
                  title: 'Configure Settings',
                  description: 'Set ads per mile and auto-pull options',
                  status: selectedTripIds.length > 0 ? 'finish' : 'wait',
                },
                {
                  title: 'Save or Launch',
                  description: 'Save campaign or export to ad platform',
                  status: 'wait',
                },
              ]}
            />

            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text strong>Ready to launch or save?</Text>
              <Text type="secondary">
                Save this campaign for later management or export to Facebook/Google Ads immediately.
              </Text>
            </Space>

            <Space wrap>
              <Button
                type="primary"
                disabled={selectedTripIds.length === 0}
                onClick={openSaveCampaignModal}
              >
                Save Campaign
              </Button>
              <Button
                disabled={selectedTripIds.length === 0}
                onClick={() => handleExportToCampaigns('facebook')}
              >
                Launch Facebook Campaign
              </Button>
              <Button
                disabled={selectedTripIds.length === 0}
                onClick={() => handleExportToCampaigns('google')}
              >
                Launch Google Campaign
              </Button>
            </Space>

            {selectedTripIds.length === 0 && (
              <Text type="secondary">
                Select one or more trips above to enable campaign export options.
              </Text>
            )}
          </Space>
        </Card>

        {/* Saved Campaigns */}
        <Card 
          title="Saved Campaigns" 
          loading={loadingSavedCampaigns}
          extra={
            <Button size="small" onClick={fetchSavedCampaigns}>
              Refresh
            </Button>
          }
        >
          <Table
            dataSource={savedCampaigns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            columns={[
              {
                title: 'Campaign Name',
                dataIndex: 'name',
                key: 'name',
                render: (text, record) => (
                  <Space direction="vertical" size={0}>
                    <Text strong>{text}</Text>
                    {record.description && <Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text>}
                  </Space>
                ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status) => {
                  const colors = { active: 'green', paused: 'orange', draft: 'blue', completed: 'default' };
                  return <Tag color={colors[status] || 'default'}>{status?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Ads/Mile',
                dataIndex: 'ads_per_mile',
                key: 'ads_per_mile',
                render: (val) => val || 1,
              },
              {
                title: 'Auto-Pull',
                key: 'auto_pull',
                render: (_, record) => (
                  record.auto_pull_enabled ? (
                    <Tag color="blue">Every {record.auto_pull_days} days</Tag>
                  ) : (
                    <Tag>Manual</Tag>
                  )
                ),
              },
              {
                title: 'Schedule',
                key: 'schedule',
                render: (_, record) => (
                  <Space direction="vertical" size={0}>
                    {record.start_date && (
                      <Text style={{ fontSize: 12 }}>Start: {dayjs(record.start_date).format('M/D/YYYY')}</Text>
                    )}
                    {record.end_date && (
                      <Text style={{ fontSize: 12 }}>End: {dayjs(record.end_date).format('M/D/YYYY')}</Text>
                    )}
                    {!record.start_date && !record.end_date && <Text type="secondary">No schedule</Text>}
                  </Space>
                ),
              },
              {
                title: 'Engagement',
                key: 'engagement',
                render: (_, record) => {
                  const engagement = record.engagement_count || 0;
                  return (
                    <Space direction="vertical" size={0}>
                      <Text strong style={{ fontSize: 16 }}>{engagement}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>people engaging</Text>
                    </Space>
                  );
                },
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                  <Space size="small" wrap>
                    {record.status === 'active' ? (
                      <Button 
                        size="small" 
                        onClick={() => handleUpdateCampaignStatus(record.id, 'paused')}
                      >
                        Pause
                      </Button>
                    ) : (
                      <Button 
                        size="small" 
                        type="primary"
                        onClick={() => handleUpdateCampaignStatus(record.id, 'active')}
                      >
                        Start
                      </Button>
                    )}
                    <Button 
                      size="small"
                      onClick={() => openEditCampaignModal(record)}
                    >
                      Edit
                    </Button>
                    <Popconfirm
                      title="Delete this campaign?"
                      onConfirm={() => handleDeleteCampaign(record.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>

      {/* Save Campaign Modal */}
      <Modal
        title={editingCampaignId ? "Edit Campaign" : "Save Campaign"}
        open={saveCampaignModalOpen}
        onOk={handleSaveCampaign}
        onCancel={() => {
          setSaveCampaignModalOpen(false);
          campaignForm.resetFields();
          setEditingCampaignId(null);
        }}
        okText={editingCampaignId ? "Update Campaign" : "Save Campaign"}
        okButtonProps={{ loading: savingCampaign }}
        width={600}
      >
        <Form
          form={campaignForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            label="Campaign Name"
            name="name"
            rules={[{ required: true, message: 'Please enter campaign name' }]}
          >
            <Input placeholder="e.g., Weekly Route Campaign" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Form.Item
            label="Ads Per Mile"
            name="ads_per_mile"
            rules={[{ required: true, message: 'Please set ads per mile' }]}
          >
            <InputNumber
              min={0.1}
              max={10}
              step={0.1}
              style={{ width: '100%' }}
              addonAfter="per mile"
            />
          </Form.Item>

          <Form.Item
            label="Auto-Pull Routes"
            name="auto_pull_enabled"
            valuePropName="checked"
            extra="Automatically pull vehicle routes from the past N days"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.auto_pull_enabled !== currentValues.auto_pull_enabled
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('auto_pull_enabled') ? (
                <Form.Item
                  label="Pull Routes From Past (days)"
                  name="auto_pull_days"
                  rules={[{ required: true, message: 'Please set number of days' }]}
                  extra="e.g., 7 days = pull routes from the last 7 days"
                >
                  <InputNumber
                    min={1}
                    max={90}
                    style={{ width: '100%' }}
                    addonAfter="days"
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            label="Start Date"
            name="start_date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="End Date"
            name="end_date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VehicleHistory;